import {
  api,
  OsmMonitoringDto,
  OsmMonitoringsResponse,
  OsmMonitoringDetailsDto,
  CreateOsmMonitoringRequest,
  UpdateOsmMonitoringRequest,
  GetOsmMonitoringTypesResponse,
  OsmMonitoringAvailableResultsForTypeResponse,
  OsmMonitoringType,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

const osmMonitoringApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    osmMonitoringList: builder.query<OsmMonitoringDto[], { examinerId: number }>({
      query: ({ examinerId }) => api.osmMonitoring.getOsmMonitorings({ examinerId }),
      transformResponse: (response: OsmMonitoringsResponse) => response.osmMonitorings,
      providesTags: ['OsmMonitoring'],
    }),
    osmMonitoring: builder.query<OsmMonitoringDetailsDto, { id: number }>({
      query: ({ id }) => api.osmMonitoring.getOsmMonitoringDetails({ id }),
      providesTags: ['OsmMonitoring'],
    }),
    createOsmMonitoring: builder.mutation<string, CreateOsmMonitoringRequest>({
      query: (request) => api.osmMonitoring.createOsmMonitoring(request),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    updateOsmMonitoring: builder.mutation<
      void,
      { id: number; request: UpdateOsmMonitoringRequest }
    >({
      query: ({ id, request }) => api.osmMonitoring.updateOsmMonitoring({ id }, request),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    deleteOsmMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.osmMonitoring.deleteOsmMonitoring({ id }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    restoreDeletedOsmMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.osmMonitoring.restoreDeleted({ id }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    restoreArchivedOsmMonitoring: builder.mutation<void, { id: number }>({
      query: ({ id }) => api.osmMonitoring.restoreArchived({ id }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    bulkArchiveOsmMonitoring: builder.mutation<void, { monitoringIds: number[] }>({
      query: ({ monitoringIds }) => api.osmMonitoring.archiveOsmMonitoring({ monitoringIds }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    updateOsmMonitoringFollowUp: builder.mutation<void, { id: number; hasFollowUp: boolean }>({
      query: ({ id, hasFollowUp }) =>
        api.osmMonitoring.updateOsmMonitoringFollowUp({ id }, { hasFollowUp }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    bulkDeleteOsmMonitoring: builder.mutation<void, { ids: number[] }>({
      query: ({ ids }) => api.osmMonitoring.bulkDelete({ osMonitoringIds: ids }),
      invalidatesTags: ['OsmMonitoring', 'ExaminerStatus'],
    }),
    osmMonitoringTypes: builder.query<GetOsmMonitoringTypesResponse, { examinerId: number }>({
      query: ({ examinerId }) => api.osmMonitoringType.getOsmMonitoringTypes({ examinerId }),
    }),
    osmMonitoringAvailableResults: builder.query<
      OsmMonitoringAvailableResultsForTypeResponse,
      { type: OsmMonitoringType; examinerId: number; dateOfMonitoring: string }
    >({
      query: ({ type, examinerId, dateOfMonitoring }) =>
        api.osmMonitoringType.getAvailableOsmMonitoringResults({
          type,
          examinerId,
          dateOfMonitoring,
        }),
    }),
  }),
});

export const {
  useOsmMonitoringListQuery,
  useOsmMonitoringQuery,
  useCreateOsmMonitoringMutation,
  useUpdateOsmMonitoringMutation,
  useDeleteOsmMonitoringMutation,
  useRestoreDeletedOsmMonitoringMutation,
  useRestoreArchivedOsmMonitoringMutation,
  useBulkArchiveOsmMonitoringMutation,
  useUpdateOsmMonitoringFollowUpMutation,
  useBulkDeleteOsmMonitoringMutation,
  useOsmMonitoringTypesQuery,
  useOsmMonitoringAvailableResultsQuery,
} = osmMonitoringApi;
